import * as React from 'react';
import {
	Box,
	Button,
	FormField,
	Grid,
	Header,
	Hotspot,
	Icon,
	Input,
	RadioGroup,
	SpaceBetween,
	Textarea,
} from '@amzn/awsui-components-console';
import { useAuthContext } from 'src/providers/AuthContext';
import { requiredText } from 'src/utils/CommonUtils';
import { Store } from 'src/stores/store.interface';
import { useStore } from 'src/stores/store';
import { SUB_WORKFLOW_TASK_TYPE } from 'src/constants/CommonConstants';
import ConditionalAttributes from '../conditionalAttributes/conditionalAttributes';
import TaskSpecifictaskInputs from '../taskSpecifictaskInputs/taskSpecifictaskInputs';
import './updateTaskDefinition.scss';

interface UpdateTaskDefinitionProps {
	onCollapse?: () => void;
	hideDuplicate?: boolean;
}

const MAX_WAIT_MINUTES = 7 * 24 * 60; // A week

export default function UpdateTaskDefinition({
	onCollapse,
	hideDuplicate,
}: UpdateTaskDefinitionProps = {}) {
	const activeTaskDefinition = useStore(
		(state: Store) => state.activeTaskDefinition
	);
	const activeReactFlowNodeId = useStore(
		(state: Store) => state.activeReactFlowNodeId
	);
	const updateActiveTaskDefinition = useStore(
		(state: Store) => state.updateActiveTaskDefinition
	);
	const isFirstSubmissionDone = useStore(
		(state: Store) => state.isFirstSubmissionDone
	);
	const submitTaskDefinition = useStore(
		(state: Store) => state.submitTaskDefinition
	);
	const duplicateTask = useStore((state: Store) => state.duplicateTask);
	const {
		comment,
		taskDescription,
		taskName,
		batchExecutionRequired,
		taskParameter,
		stage,
		genericAPITaskName,
		notificationTask,
		individualSIMTRequired,
		regionalized,
		operation,
		taskType,
		averageExecutionTime,
	} = activeTaskDefinition;

	const authContext = useAuthContext();
	const userName = authContext.userName;

	const isWaitTask = taskType === 'WaitTask';
	const isSubWorkflowTask = taskType === SUB_WORKFLOW_TASK_TYPE;

	const updateWaitDuration = (
		part: 'days' | 'hours' | 'minutes',
		value: string
	) => {
		const parsed = Math.max(0, Math.trunc(Number(value)) || 0);
		const next = { ...averageExecutionTime, [part]: parsed };

		const totalMinutes = Math.min(
			MAX_WAIT_MINUTES,
			(next.days * 24 + next.hours) * 60 + next.minutes
		);

		updateActiveTaskDefinition('averageExecutionTime', {
			days: Math.floor(totalMinutes / (24 * 60)),
			hours: Math.floor((totalMinutes % (24 * 60)) / 60),
			minutes: totalMinutes % 60,
		});
	};

	return (
		<Box className="updateTaskDefintionCmpt" padding="m">
			<div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
				<div style={{ flex: 1, minWidth: 0 }}>
					<Header
						data-testid="activeTaskName"
						variant="h2"
						description={
							<Box fontSize="body-s" color="text-body-secondary">
								<em>Configure task parameters and execution settings. Please save your details before moving to another task.</em>
							</Box>
						}
					>
						<span style={{ wordBreak: 'break-word' }}>{taskName || 'Task'} Definition</span>
						<Hotspot side="right" hotspotId="fill-task-definition" />
					</Header>
				</div>
				{onCollapse && (
					<Button
						iconName="angle-right-double"
						variant="icon"
						onClick={onCollapse}
						ariaLabel="Close Task Definition Panel"
					/>
				)}
			</div>
			{activeReactFlowNodeId && !hideDuplicate && (
				<Box margin={{ top: 's', bottom: 's' }}>
					<Button
						data-testid="duplicateTaskDefinition"
						onClick={() => {
							duplicateTask(activeReactFlowNodeId, String(userName));
						}}
						variant="normal"
						iconName="copy"
					>
						Duplicate Task
					</Button>
				</Box>
			)}
			<hr className="section-divider" />

			<Box className="taskDefinitionContent">
				{activeReactFlowNodeId && (
					<SpaceBetween size="l">
						<Box>
							<Header variant="h3">
								<Icon name="edit" variant="subtle" /> Basic Information
							</Header>
							<Box margin={{ bottom: 'm' }} />
							<SpaceBetween size="m">
								<FormField
									description="Enter a meaningful name to identify this task"
									data-testid="taskName"
									errorText={isFirstSubmissionDone && requiredText(taskName)}
									label="Task Name"
								>
									<Input
										onChange={({ detail }) =>
											updateActiveTaskDefinition('taskName', detail.value)
										}
										value={taskName}
										type="text"
										placeholder="e.g., Validate Input Data"
									/>
								</FormField>
								<FormField
									description="The task type selected from the supported tasks list"
									label="Task Type"
								>
									<Input
										value={taskType}
										disabled
									/>
								</FormField>
								<FormField
									description="Explain what this task does in the context of this workflow"
									data-testid="taskDescription"
									errorText={isFirstSubmissionDone && requiredText(taskDescription)}
									label="Task Description"
								>
									<Textarea
										onChange={({ detail }) =>
											updateActiveTaskDefinition('taskDescription', detail.value)
										}
										value={taskDescription}
										placeholder="Describe the purpose and functionality of this task"
										rows={3}
									/>
								</FormField>
								<FormField
									description="Reason for adding or modifying this task (include SIM details if available)"
									data-testid="comment"
									errorText={isFirstSubmissionDone && requiredText(comment)}
									label="Comment"
								>
									<Textarea
										onChange={({ detail }) =>
											updateActiveTaskDefinition('comment', detail.value)
										}
										value={comment}
										placeholder="Provide context or reference tickets"
										rows={2}
									/>
								</FormField>
								<Box color="text-status-warning" fontSize="body-s" margin={{ top: 'm' }}>
									<Icon name="status-warning" variant="warning" /> All fields in the above section are mandatory
								</Box>
							</SpaceBetween>
						</Box>

						<hr className="section-divider-thick" />

						{!isSubWorkflowTask && (
						<Box>
							<Header variant="h3">
								<Icon name="settings" variant="subtle" /> Execution Settings
							</Header>
							<Box margin={{ bottom: 'm' }} />
							<SpaceBetween size="m">
								<FormField
									description="Enable batch execution if this task processes multiple records"
									data-testid="batchExecutionRequired"
									label="Batch Execution Required"
								>
									<RadioGroup
										value={String(batchExecutionRequired)}
										items={[
											{
												label: 'Yes',
												value: 'true',
											},
											{
												label: 'No',
												value: 'false',
											},
										]}
										onChange={({ detail }) =>
											updateActiveTaskDefinition(
												'batchExecutionRequired',
												detail.value
											)
										}
									/>
								</FormField>

								{isWaitTask && (
									<FormField
										description="How long this wait task pauses the workflow before completing. Minimum effective wait is ~1 minute; maximum is 7 days."
										data-testid="waitDuration"
										label="Wait Duration"
									>
										<Grid
											gridDefinition={[
												{ colspan: 4 },
												{ colspan: 4 },
												{ colspan: 4 },
											]}
										>
											<FormField label="Days" data-testid="waitDurationDays">
												<Input
													onChange={({ detail }) =>
														updateWaitDuration('days', detail.value)
													}
													value={String(averageExecutionTime?.days ?? 0)}
													type="number"
													inputMode="numeric"
													step={1}
												/>
											</FormField>
											<FormField label="Hours" data-testid="waitDurationHours">
												<Input
													onChange={({ detail }) =>
														updateWaitDuration('hours', detail.value)
													}
													value={String(averageExecutionTime?.hours ?? 0)}
													type="number"
													inputMode="numeric"
													step={1}
												/>
											</FormField>
											<FormField label="Minutes" data-testid="waitDurationMinutes">
												<Input
													onChange={({ detail }) =>
														updateWaitDuration('minutes', detail.value)
													}
													value={String(averageExecutionTime?.minutes ?? 0)}
													type="number"
													inputMode="numeric"
													step={1}
												/>
											</FormField>
										</Grid>
									</FormField>
								)}

								{!operation && (
									<>
										<FormField
											description="Specify when this task should execute in the workflow lifecycle"
											data-testid="stage"
											label="Execution Stage"
										>
											<Input
												onChange={({ detail }) =>
													updateActiveTaskDefinition('stage', detail.value)
												}
												value={stage as string}
												type="text"
												placeholder="e.g., pre-processing, validation"
											/>
										</FormField>

										<FormField
											description="Enable if this task sends notifications without waiting for response"
											data-testid="notificationTask"
											label="Notification Task"
										>
											<RadioGroup
												value={String(notificationTask)}
												items={[
													{
														label: 'Yes',
														value: 'true',
													},
													{
														label: 'No',
														value: 'false',
													},
												]}
												onChange={({ detail }) =>
													updateActiveTaskDefinition(
														'notificationTask',
														detail.value
													)
												}
											/>
										</FormField>

										<FormField
											description="Enable if individual SIMT is required for this task"
											data-testid="individualSIMTRequired"
											label="Individual SIMT Required"
										>
											<RadioGroup
												value={String(individualSIMTRequired)}
												items={[
													{
														label: 'Yes',
														value: 'true',
													},
													{
														label: 'No',
														value: 'false',
													},
												]}
												onChange={({ detail }) =>
													updateActiveTaskDefinition(
														'individualSIMTRequired',
														detail.value
													)
												}
											/>
										</FormField>

										<FormField
											description="Enable if task implementation varies by region"
											data-testid="regionalized"
											label="Regionalized Task"
										>
											<RadioGroup
												value={String(regionalized)}
												items={[
													{
														label: 'Yes',
														value: 'true',
													},
													{
														label: 'No',
														value: 'false',
													},
												]}
												onChange={({ detail }) =>
													updateActiveTaskDefinition('regionalized', detail.value)
												}
											/>
										</FormField>
									</>
								)}
							</SpaceBetween>
						</Box>
						)}

						<hr className="section-divider-thick" />

						{!operation && !isSubWorkflowTask && (
							<Box>
							<Header variant="h3">
								<Icon name="settings" variant="subtle" /> Advanced Configuration
							</Header>
							<Box color="text-body-secondary" fontSize="body-s" margin={{ bottom: 'm' }}>
								<em>Optional parameters for advanced task configuration</em>
							</Box>
								<Box className="taskDefinitionSection taskDefinitionSectionNested">
								<SpaceBetween size="m">
									<FormField
										description="Parameters required for task execution"
										data-testid="taskParameter"
										label="Task Parameters"
									>
										<Textarea
											onChange={({ detail }) =>
												updateActiveTaskDefinition(
													'taskParameter',
													detail.value
												)
											}
											value={taskParameter as string}
											placeholder="Enter task parameters"
											rows={3}
										/>
									</FormField>

									<FormField
										description="API task name for configuration updates"
										data-testid="genericAPITaskName"
										label="Generic API Task Name"
									>
										<Input
											onChange={({ detail }) =>
												updateActiveTaskDefinition(
													'genericAPITaskName',
													detail.value
												)
											}
											value={genericAPITaskName as string}
											type="text"
											placeholder="Enter API task name"
										/>
									</FormField>
								</SpaceBetween>
								</Box>
							<hr className="section-divider-thick" />
							</Box>
						)}


						<Box>
							<ConditionalAttributes />
						</Box>

						<hr className="section-divider-thick" />

						<Box>
							<Header variant="h3">
								<Icon name="mini-player" variant="subtle" />{' '}
								{isSubWorkflowTask ? 'SubWorkflow Inputs' : 'Task Specific Inputs'}
							</Header>
							<Box color="text-body-secondary" fontSize="body-s" margin={{ bottom: 'm' }}>
								<em>Note: Leave inputs empty if end users need to provide values during execution</em>
							</Box>
							<TaskSpecifictaskInputs />
						</Box>

						<Box className="taskDefinitionActions">
							<SpaceBetween direction="horizontal" size="s">
								<Button
									data-testid="saveTaskDefinition"
									onClick={() => {
										submitTaskDefinition(String(userName));
									}}
									variant="primary"
									iconName="check"
								>
									Save Task Definition
								</Button>
							</SpaceBetween>
						</Box>
					</SpaceBetween>
				)}
				{!activeReactFlowNodeId && (
					<Box textAlign="center" padding={{ vertical: 'xxl' }}>
						<SpaceBetween size="m" alignItems="center">
							<Icon name="status-info" size="big" variant="subtle" />
							<Box variant="h3" color="text-body-secondary">
								No Task Selected
							</Box>
							<Box color="text-body-secondary" fontSize="body-m">
								Select a task from the workflow canvas to view and edit its definition
							</Box>
						</SpaceBetween>
					</Box>
				)}
			</Box>
		</Box>
	);
}
